import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Eye, 
  CheckCircle, 
  Clock,
  Truck,
  Package,
  Star,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { formatPrice, formatDate } from '../../lib/utils';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'preparing', label: 'Preparing' },
    { value: 'out', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' }
  ];

  const fetchOrders = useCallback(async () => {
    try {
      // Check multiple sources for customer phone
      let customerPhone = user?.phoneNumber || 
                         user?.phone || 
                         localStorage.getItem('customerPhone') ||
                         localStorage.getItem('userPhone');
      
      if (!customerPhone) {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          try {
            const parsedUser = JSON.parse(storedUserData);
            customerPhone = parsedUser.phone || parsedUser.phoneNumber;
          } catch (error) {
            console.error('Error parsing stored user data:', error);
          }
        }
      }
      
      console.log('🔍 OrderHistoryPage - User object:', user);
      console.log('🔍 OrderHistoryPage - Customer phone:', customerPhone);
      
      if (!customerPhone) {
        console.log('No customer phone found, redirecting to login');
        setError('Please login to view your orders');
        setLoading(false);
        return;
      }

      const ordersQuery = query(
        collection(db, 'orders'), 
        where('customerPhone', '==', customerPhone)
      );
      const querySnapshot = await getDocs(ordersQuery);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt (newest first) on client side
      ordersData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return dateB - dateA;
      });
      
      setOrders(ordersData);
      console.log('Fetched orders for phone:', customerPhone, 'Count:', ordersData.length);
      console.log('Orders data:', ordersData);
      console.log('First order structure:', ordersData[0]);
      if (ordersData[0]) {
        console.log('First order total:', ordersData[0].total);
        console.log('First order totalAmount:', ordersData[0].totalAmount);
        console.log('First order subtotal:', ordersData[0].subtotal);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const filterOrders = useCallback(() => {
    let filtered = orders;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(order => 
        order.orderId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  }, [orders, searchQuery, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchQuery, statusFilter, filterOrders]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': 
        return { bg: '#fef3c7', text: '#d97706' };
      case 'accepted': 
        return { bg: '#dbeafe', text: '#2563eb' };
      case 'preparing': 
        return { bg: '#e0e7ff', text: '#7c3aed' };
      case 'out': 
        return { bg: '#fed7aa', text: '#ea580c' };
      case 'delivered': 
        return { bg: '#d1fae5', text: '#059669' };
      default: 
        return { bg: '#f3f4f6', text: '#6b7280' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'accepted': return <CheckCircle className="w-4 h-4" />;
      case 'preparing': return <Package className="w-4 h-4" />;
      case 'out': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };


  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <div style={{ padding: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                Order Details
              </h2>
              <button
                onClick={onClose}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Order Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px'
                  }}>
                    Order Information
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Order ID:</span> {order.orderId}
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Date:</span> {formatDate(order.createdAt)}
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Status:</span> 
                      <span style={{
                        marginLeft: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getStatusColor(order.status).bg,
                        color: getStatusColor(order.status).text
                      }}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Payment Method:</span> 
                      <span style={{
                        marginLeft: '8px',
                        fontWeight: '600',
                        color: order.paymentMethod === 'COD' ? '#059669' : '#3b82f6'
                      }}>
                        {order.paymentMethod === 'COD' ? 'Cash on Delivery' : 
                         order.paymentMethod === 'razorpay' ? 'Razorpay' : 
                         order.paymentMethod?.toUpperCase() || 'COD'}
                      </span>
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Payment Status:</span> 
                      <span style={{
                        marginLeft: '8px',
                        color: order.status === 'delivered' ? '#059669' : '#d97706',
                        fontWeight: '600'
                      }}>
                        {order.status === 'delivered' ? 'Completed' : 'Pending'}
                      </span>
                    </p>
                  </div>
                </div>
                
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px'
                  }}>
                    Delivery Address
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Name:</span> {order.deliveryAddress?.name || 'N/A'}
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Phone:</span> {order.deliveryAddress?.phone || 'N/A'}
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>Address:</span> {order.deliveryAddress?.address || 'N/A'}
                    </p>
                    <p style={{ fontSize: '14px', margin: 0 }}>
                      <span style={{ color: '#6b7280' }}>City:</span> {order.deliveryAddress?.city || 'N/A'} - {order.deliveryAddress?.pincode || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 12px'
                }}>
                  Order Items
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {order.items?.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      <div>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: '0 0 4px'
                        }}>
                          {item.name}
                        </p>
                        <p style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          margin: 0
                        }}>
                          {item.portion} • Qty: {item.quantity}
                        </p>
                      </div>
                      <p style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937',
                        margin: 0
                      }}>
                        {formatPrice((typeof item.price === 'object' ? 
                          (item.price[item.portion] || item.price.half || 0) : 
                          (item.price || 0)) * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div style={{
                borderTop: '1px solid #e5e7eb',
                paddingTop: '16px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '14px'
                  }}>
                    <span>Subtotal:</span>
                    <span>{formatPrice(order.subtotal || 0)}</span>
                  </div>
                  {(order.discount || 0) > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '14px',
                      color: '#059669'
                    }}>
                      <span>Discount:</span>
                      <span>-{formatPrice(order.discount)}</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '18px',
                    fontWeight: '700',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '8px'
                  }}>
                    <span>Total:</span>
                    <span style={{ color: '#ff6b35' }}>{formatPrice(order.total || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                {order.status === 'delivered' && (
                  <Button
                    onClick={() => {
                      navigate('/');
                      onClose();
                    }}
                    style={{ flex: 1 }}
                    variant="default"
                  >
                    <RefreshCw style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                    Reorder Items
                  </Button>
                )}
                
                <Button
                  onClick={() => {
                    navigate('/');
                    onClose();
                  }}
                  variant="outline"
                  style={{ flex: 1 }}
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  console.log('🔍 OrderHistoryPage render - Loading:', loading, 'Orders:', orders.length, 'Filtered:', filteredOrders.length, 'Error:', error);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Orders</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <div className="space-x-3">
            <Button
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchOrders();
              }}
              variant="default"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/order')}
              variant="outline"
            >
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Modern Header */}
      <header style={{
        background: 'linear-gradient(135deg, #ff6b35 0%, #dc2626 50%, #ec4899 100%)',
        padding: '16px 20px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <button
            onClick={() => navigate('/order')}
            style={{
              padding: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: 'white',
            margin: 0,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            Order History
          </h1>
        </div>
      </header>

      <main style={{
        padding: '24px 20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Modern Filters */}
        <div style={{
          marginBottom: '32px',
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {/* Search Bar */}
            <div style={{
              position: 'relative',
              flex: 1
            }}>
              <Search style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                width: '20px',
                height: '20px'
              }} />
              <input
                type="text"
                placeholder="Search orders by ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px 16px 16px 48px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#f9fafb'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b35';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
              />
            </div>
            
            {/* Status Filter Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: statusFilter === option.value ? '#ff6b35' : '#f3f4f6',
                    color: statusFilter === option.value ? 'white' : '#6b7280',
                    boxShadow: statusFilter === option.value ? '0 4px 12px rgba(255, 107, 53, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (statusFilter !== option.value) {
                      e.currentTarget.style.backgroundColor = '#e5e7eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (statusFilter !== option.value) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {filteredOrders.length === 0 ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '48px 24px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                width: '96px',
                height: '96px',
                backgroundColor: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <Package style={{
                  width: '48px',
                  height: '48px',
                  color: '#9ca3af'
                }} />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                margin: '0 0 8px'
              }}>
                {orders.length === 0 ? 'No Orders Yet' : 'No Orders Found'}
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                margin: '0 0 24px'
              }}>
                {orders.length === 0 
                  ? 'Start by placing your first order' 
                  : 'No orders match your current filters'
                }
              </p>
              <button
                onClick={() => navigate('/order')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e55a2b'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
              >
                {orders.length === 0 ? 'Start Shopping' : 'Clear Filters'}
              </button>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  padding: '24px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                }}
                onClick={() => setSelectedOrder(order)}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1f2937',
                      margin: '0 0 4px'
                    }}>
                      Order #{order.orderId}
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      {new Date(order.createdAt?.seconds * 1000).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div style={{
                    padding: '8px 16px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: getStatusColor(order.status).bg,
                    color: getStatusColor(order.status).text
                  }}>
                    {order.status.toUpperCase()}
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div>
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0 0 4px'
                    }}>
                      {order.items?.length || 0} items • {order.paymentMethod === 'COD' ? 'Cash on Delivery' : 
                                                      order.paymentMethod === 'razorpay' ? 'Razorpay' : 
                                                      order.paymentMethod?.toUpperCase() || 'COD'}
                    </p>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#1f2937',
                      margin: '0'
                    }}>
                      ₹{order.total || 0}
                    </p>
                  </div>
                  <button
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e5e7eb';
                      e.currentTarget.style.color = '#374151';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.color = '#6b7280';
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)} 
        />
      )}
    </div>
  );
};

export default OrderHistoryPage;
